import axios from "axios";
import { getCurrentUser, authReady } from "./firebase/auth";
import { DEMO_MODE } from "@demo/config";
import demoAdapter from "@demo/adapter";
import { LOCAL_MODE } from "@local/config";
import createLocalAdapter from "@local/adapter";

// Auto-detect environment: Desktop development uses port 8080 for backend
const API_PORT = process.env.VUE_APP_API_PORT || '8080';
const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost';
const BASE_URL = isDevelopment
  ? `http://localhost:${API_PORT}/api`
  : 'https://inr-backend-604839735443.northamerica-northeast1.run.app/api';

// Whether to attach the dev fallback auth header (x-user-id: dev-user-1) when
// no Firebase user is signed in. This MUST never be true in a production build:
// `process.env.NODE_ENV === 'development'` is baked in at build time and cannot
// be spoofed by a client setting hostname to "localhost". We additionally
// require running on localhost, and allow an explicit opt-out/opt-in via
// VUE_APP_USE_DEV_AUTH (defaults to enabled only in a dev build).
const USE_DEV_AUTH =
  process.env.NODE_ENV === 'development' &&
  window.location.hostname === 'localhost' &&
  process.env.VUE_APP_USE_DEV_AUTH !== 'false';

const axiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-type": "application/json"
  }
});

// Public demo build: answer every request from the in-memory clinic instead of
// the network. Installed as an axios ADAPTER rather than as a branch in each
// service, so the ~30 modules that call this instance keep their production
// code path exactly — the request is built, interceptors run, and only the
// transport is different. Nothing can leak to a real backend from this build.
if (DEMO_MODE) {
  axiosInstance.defaults.adapter = demoAdapter;
}

// On-premise build: answer every clinical request from IndexedDB in THIS
// browser, and forward only the licence / install / slot / telemetry paths to
// the network. src/local/adapter.js holds that allowlist and is the file where
// "patient data never leaves this computer" is either kept or broken.
//
// Installed as an ADAPTER for the same reason the demo is: the ~30 modules that
// call this instance keep their production code path exactly — the request is
// built, interceptors run, and only the transport differs.
//
// The network adapter is captured BEFORE being replaced and handed to the local
// one, so passthrough requests go out with the same auth header, base URL and
// interceptors as any other request. Resolved through axios.getAdapter because
// `defaults.adapter` is a NAME (or list of names) in axios 1.x, not a function.
if (LOCAL_MODE) {
  const configured = axiosInstance.defaults.adapter || axios.defaults.adapter;
  const networkAdapter =
    typeof configured === 'function'
      ? configured
      : (typeof axios.getAdapter === 'function' ? axios.getAdapter(configured) : null);
  axiosInstance.defaults.adapter = createLocalAdapter(networkAdapter);
}

/**
 * The headers an authenticated request carries.
 *
 * EXPORTED, and that is the point. One request in the app cannot go through
 * axios: the backup import streams SSE progress, so RestoreLanding.vue drives
 * it with `fetch` and used to assemble its own headers. That copy drifted —
 * it sent x-user-id and the display name but NOT the Firebase bearer token, so
 * POST /backup/import answered 401 for a pharmacist who was signed in and whose
 * every other request succeeded. The backend's x-user-id fallback only exists
 * when the SERVER runs in development, which is why it looked like it worked
 * for whoever wrote it.
 *
 * Re-adding one missing header there would have fixed the symptom and left the
 * second copy. This is the single source of truth instead; anything that has to
 * bypass axios calls it.
 */
export async function buildAuthHeaders() {
  const headers = {};

  // No credentials exist in the demo build, and awaiting Firebase there would
  // hang on an SDK that was never initialised.
  if (DEMO_MODE) return headers;

  try {
    // Firebase restores a session asynchronously, and `getCurrentUser()` reads
    // null until it has. A request that leaves during that window carries no
    // Authorization header and comes back 401 — which the screen renders as a
    // failure even though the user is perfectly signed in. Waiting here costs
    // nothing after the first resolution (the promise is already settled) and
    // is bounded by a timeout inside `authReady`, so a broken Firebase config
    // still lets requests through rather than hanging them.
    await authReady;
    const user = getCurrentUser();
    if (user) {
      // Get a fresh ID token (Firebase SDK handles caching and refresh)
      const idToken = await user.getIdToken();
      headers['Authorization'] = `Bearer ${idToken}`;

      // Still send x-user-id for backward compatibility during migration
      headers['x-user-id'] = user.uid;

      // Check for siteadmin custom claim and add header
      try {
        const idTokenResult = await user.getIdTokenResult();
        if (idTokenResult.claims?.siteadmin === true) {
          headers['x-is-siteadmin'] = 'true';
        }
      } catch (e) {
        // Silent fail - siteadmin check is not critical
      }

      // Add user email for account creation
      if (user.email) {
        headers['x-user-email'] = user.email;
      }

      // Add user display name and organization name for email templates
      // Try multiple sources: localStorage, Firebase displayName, or extract from email
      const storedUserName = localStorage.getItem('user_display_name');
      if (storedUserName) {
        headers['x-user-name'] = storedUserName;
      } else if (user.displayName) {
        headers['x-user-name'] = user.displayName;
      } else if (user.email) {
        // Fallback: use part before @ in email
        headers['x-user-name'] = user.email.split('@')[0];
      }

      // Add owner's first name if available
      const storedFirstName = localStorage.getItem('owner_first_name');
      if (storedFirstName) {
        headers['x-owner-first-name'] = storedFirstName;
      }

      // Add organization name if available
      const storedOrgName = localStorage.getItem('organization_name');
      if (storedOrgName) {
        headers['x-organization-name'] = storedOrgName;
      }
    } else if (USE_DEV_AUTH) {
      // Use default userId for local development when not logged in
      headers['x-user-id'] = 'dev-user-1';
      headers['x-user-name'] = 'Dev User';
    }
  } catch (err) {
    // Firebase not initialized, use dev user only in a genuine local dev build
    if (USE_DEV_AUTH) {
      headers['x-user-id'] = 'dev-user-1';
      headers['x-user-name'] = 'Dev User';
    }
  }

  return headers;
}

// Add request interceptor to include user ID and name in headers
axiosInstance.interceptors.request.use(
  async (config) => {
    const auth = await buildAuthHeaders();

    // `config.headers` is an AxiosHeaders instance in the app and a plain object
    // in the interceptor's own tests; `set` is used where it exists so the
    // header goes through axios's normalisation rather than around it.
    Object.entries(auth).forEach(([name, value]) => {
      if (config.headers && typeof config.headers.set === 'function') {
        config.headers.set(name, value);
      } else {
        config.headers = config.headers || {};
        config.headers[name] = value;
      }
    });

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to log AI request failures
axiosInstance.interceptors.response.use(
  (response) => {
    // Note: successful AI response payloads are intentionally NOT logged to
    // avoid leaking full (potentially sensitive) AI response data to the console.
    return response;
  },
  (error) => {
    // Log AI errors
    if (error.config?.url?.includes('/ai/')) {
      console.error('[AI HTTP DEBUG] Request failed:', {
        url: error.config.url,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
